import {
  AuthSessionManager,
  type AuthSessionSnapshot,
} from "@app/AuthSessionManager.ts";
import { trpcUtils } from "@app/trpcUtils.ts";
import type { AuthSession } from "@server/models/auth.ts";

export type AuthSessionRefresher = (
  snapshot: AuthSessionSnapshot,
) => Promise<boolean>;

export function createAuthSessionRefresher(
  refresh: (refreshToken: string) => Promise<AuthSession>,
): AuthSessionRefresher {
  let ongoing: { version: number; promise: Promise<boolean> } | null = null;

  return (snapshot) => {
    if (!AuthSessionManager.isCurrent(snapshot)) {
      return Promise.resolve(false);
    }
    if (AuthSessionManager.getAuthToken() !== snapshot.authToken) {
      return Promise.resolve(AuthSessionManager.getAuthToken() !== null);
    }
    if (ongoing?.version === snapshot.version) {
      return ongoing.promise;
    }
    if (!snapshot.refreshToken) {
      AuthSessionManager.clear();
      return Promise.resolve(false);
    }

    const promise = refresh(snapshot.refreshToken)
      .then((session) => AuthSessionManager.replace(session, snapshot))
      .catch((error: unknown) => {
        if (!AuthSessionManager.isCurrent(snapshot)) {
          return false;
        }
        if (trpcUtils.isUnauthorized(error)) {
          if (AuthSessionManager.getRefreshToken() === snapshot.refreshToken) {
            AuthSessionManager.clear();
          }
          return false;
        }
        throw error;
      })
      .finally(() => {
        if (ongoing?.promise === promise) {
          ongoing = null;
        }
      });
    ongoing = { version: snapshot.version, promise };
    return promise;
  };
}
